import React from 'react';
import { Loader2, Stamp, Info } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';

export interface ReglagesFidelite {
    enabled: boolean;
    mode: 'tampons' | 'points';
    threshold: number;
    points_per_euro: number;
    reward_type: 'percent' | 'amount';
    reward_value: number;
    audience: 'tous' | 'particulier' | 'professionnel';
    exclude_formula: boolean;
}

const DEFAUT: ReglagesFidelite = {
    enabled: false, mode: 'tampons', threshold: 10, points_per_euro: 1,
    reward_type: 'percent', reward_value: 10, audience: 'tous', exclude_formula: true,
};

/**
 * Carte de fidélité (2026-09-24, Emeni) — dernier des 8 points demandés le 22/09.
 *
 * Deux façons de compter, au choix : des TAMPONS (un par rendez-vous terminé, la carte du coiffeur) ou
 * des POINTS (proportionnels au montant dépensé, plus juste quand les prix vont du simple au décuple).
 * La récompense est une réduction — pourcentage ou montant — appliquée automatiquement à la prestation
 * SUIVANTE : une carte se remplit d'abord, se dépense ensuite.
 */
export const FideliteSettings: React.FC = () => {
    const [r, setR] = React.useState<ReglagesFidelite>(DEFAUT);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getLoyaltySettings()
            .then((d: any) => setR({ ...DEFAUT, ...(d || {}) }))
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const maj = (patch: Partial<ReglagesFidelite>) => setR((x) => ({ ...x, ...patch }));

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.saveLoyaltySettings(r);
            if (res?.error) { setErreur(res.error); return; }
            setR({ ...DEFAUT, ...(res || {}) });
            setSaved(true); setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-content-muted" /></div>;

    const champ = 'w-28 bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content text-right outline-none focus:border-brand-500';
    const Choix = <T extends string>({ valeur, options, onChange }: { valeur: T; options: ReadonlyArray<readonly [T, string]>; onChange: (v: T) => void }) => (
        <div className="flex gap-1.5 bg-surface rounded-xl p-1 flex-wrap">
            {options.map(([v, label]) => (
                <button key={v} type="button" onClick={() => onChange(v)}
                    className={`flex-1 min-w-[120px] px-3 py-2 rounded-lg text-xs font-bold transition-colors ${valeur === v ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    {label}
                </button>
            ))}
        </div>
    );

    const recompense = r.reward_type === 'percent' ? `${r.reward_value} %` : `${r.reward_value} €`;
    const exemple = r.mode === 'points'
        ? `Un client qui dépense 100 € gagne ${Math.round(100 * r.points_per_euro)} points. À ${r.threshold} points, il obtient ${recompense} de réduction sur sa prestation suivante.`
        : `Au ${Math.round(r.threshold)}ᵉ rendez-vous terminé, le client obtient ${recompense} de réduction sur sa prestation suivante.`;

    return (
        <div className="space-y-5">
            <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={r.enabled} onChange={(e) => maj({ enabled: e.target.checked })} className="accent-brand-600 w-4 h-4" />
                    <span className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Stamp size={13} /> Carte de fidélité</span>
                </label>
                <p className="text-xs text-content-muted italic">
                    Le compte avance tout seul à chaque rendez-vous terminé, et la réduction se pose toute seule sur la facture
                    suivante. Vous n'avez rien à suivre à la main.
                </p>

                {r.enabled && (
                    <div className="space-y-5 pt-2">
                        <div className="space-y-2">
                            <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Comment on compte</p>
                            <Choix valeur={r.mode} onChange={(v) => maj({ mode: v })}
                                options={[['tampons', 'Par rendez-vous'], ['points', 'Par montant dépensé']] as const} />
                            {r.mode === 'tampons' ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-content-muted">Réduction tous les</span>
                                    <input type="number" min={1} step={1} value={r.threshold} onChange={(e) => maj({ threshold: Number(e.target.value) })} className={champ} />
                                    <span className="text-xs text-content-muted">rendez-vous terminés</span>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs text-content-muted">1 € dépensé rapporte</span>
                                        <input type="number" min={0} step={0.1} value={r.points_per_euro} onChange={(e) => maj({ points_per_euro: Number(e.target.value) })} className={champ} />
                                        <span className="text-xs text-content-muted">point(s)</span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs text-content-muted">Réduction à partir de</span>
                                        <input type="number" min={1} step={1} value={r.threshold} onChange={(e) => maj({ threshold: Number(e.target.value) })} className={champ} />
                                        <span className="text-xs text-content-muted">points</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <p className="text-xs font-bold text-content-muted uppercase tracking-widest">La récompense</p>
                            <Choix valeur={r.reward_type} onChange={(v) => maj({ reward_type: v })}
                                options={[['percent', 'Un pourcentage'], ['amount', 'Un montant fixe']] as const} />
                            <div className="flex items-center gap-2">
                                <input type="number" min={0} step={r.reward_type === 'percent' ? 1 : 0.5} max={r.reward_type === 'percent' ? 100 : undefined}
                                    value={r.reward_value} onChange={(e) => maj({ reward_value: Number(e.target.value) })} className={champ} />
                                <span className="text-xs text-content-muted">{r.reward_type === 'percent' ? '% de réduction' : '€ de réduction'}</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Pour qui</p>
                            <Choix valeur={r.audience} onChange={(v) => maj({ audience: v })}
                                options={[['tous', 'Tout le monde'], ['particulier', 'Particuliers'], ['professionnel', 'Professionnels']] as const} />
                            <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                                <input type="checkbox" checked={r.exclude_formula} onChange={(e) => maj({ exclude_formula: e.target.checked })} className="accent-brand-600 mt-0.5" />
                                <span className="text-[11px] text-content-muted">
                                    <b className="text-content">Laisser de côté les clients qui ont une formule</b> — ils bénéficient déjà de
                                    réductions à chaque passage ; cumuler les deux reviendrait à payer deux fois la même fidélité.
                                </span>
                            </label>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-xl bg-surface border border-border text-[11px] text-content-muted">
                            <Info size={14} className="shrink-0 mt-0.5" />
                            <span>
                                {exemple} La réduction s'applique à la prestation <b className="text-content">suivante</b> : une carte se remplit
                                d'abord, se dépense ensuite. Pour dire au client où il en est, placez le jeton <b className="text-content">{'{{fidelite}}'}</b> dans
                                vos emails (Paramètres &gt; Notifications) : il écrit « 7 passages sur 10 — encore 3 avant votre réduction »,
                                et ne laisse rien quand le client n'est pas concerné.
                            </span>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-3 pt-1">
                    <Button disabled={saving} onClick={enregistrer}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : null} {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    {saved && <span className="text-xs font-bold text-green-500">Enregistré ✓</span>}
                </div>
                {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
            </div>
        </div>
    );
};
